import React, { useMemo } from "react";
import ViewEditor from "../../components/common/editor/ViewEditor";
import Marks from "../../components/common/tabs/Marks.js";
import { questionTypeConstants } from "../../helper/constants.js";
import AssessmentInfoCard from "../assessment/components/AssessmentInfoCard.js";

const SelfGradeGroupQuestionView = ({
  sections = [],
  assessment_details = {},
  assessmentQuestions = [],
  subQuestions = {},
  getEditability,
  isMCQReadOnly,
  getContentInEditor,
  getAnswerInEditor,
  getAssessorAnswerInEditor,
  getAssessorAnswerGuidanceInEditor,
  getQuestionPassageContent,
  onChangeMarks,
  getSelectedQuestionIdsForMarking,
  state = {},
  candidateAnswerMap
}) => {
  const groupedQuestions = useMemo(() => {
    const result = {};

    assessmentQuestions.forEach((question, index) => {
      const sectionMeta = sections.find(
        s => s.section_id === question.section_id
      );

      const partId = sectionMeta?.part_id;
      const partName = sectionMeta?.part_name ?? "Unnamed Part";
      const sectionId = question.section_id;
      const sectionTitle = sectionMeta?.section_title ?? "Untitled Section";
      const allowPartInstruction = sectionMeta?.allow_part_instruction ?? false;
      const partInstruction = sectionMeta?.part_instruction ?? "";
      const allowSectionInstruction = sectionMeta?.allow_section_instruction ?? false;
      const sectionInstruction = sectionMeta?.section_instruction ?? "";
      const part_display_order = sectionMeta?.part_display_order;
      const section_display_order = sectionMeta?.section_display_order;

      if (!result[partId]) {
        result[partId] = {
          name: partName,
          allow_part_instruction: allowPartInstruction,
          part_instruction: partInstruction,
          part_display_order: part_display_order,
          sections: {}
        };
      }

      if (!result[partId].sections[sectionId]) {
        result[partId].sections[sectionId] = {
          title: sectionTitle,
          allow_section_instruction: allowSectionInstruction,
          section_instruction: sectionInstruction,
          section_display_order: section_display_order,
          questions: []
        };
      }

      result[partId].sections[sectionId].questions.push({ data: question, index });
    });

    return result;
  }, [assessmentQuestions]);

  let questionCounter = 0;

  return (
    <>
      <AssessmentInfoCard details={assessment_details} /> 

      {Object.entries(groupedQuestions).sort(([, a], [, b]) => a.part_display_order - b.part_display_order).map(([partId, part]) => (
        <div key={`part-${partId}`} className="mb-5 p-4 border rounded bg-light">
          <h2 className="text-primary mb-4">{part.name}</h2>

          {part.allow_part_instruction && part.part_instruction && (
            <div className="mb-4">
              <ul className="mb-3" style={{ paddingLeft: "1.5rem" }}>
                {part.part_instruction.split('\n').map((line, idx) => (
                  <li key={`part-inst-${idx}`}>{line.replace(/^•\s*/, '').trim()}</li>
                ))}
              </ul>
            </div>
          )}

          {Object.entries(part.sections).sort(([, a], [, b]) => a.section_display_order - b.section_display_order).map(([sectionId, section]) => (
            <div key={`section-${sectionId}`} className="mb-4 p-3 border rounded bg-white">
              <h4 className="text-secondary">{section.title}</h4>

              {section.allow_section_instruction && section.section_instruction && (
                <div className="mb-3">
                  <ul className="mb-3" style={{ paddingLeft: "1.5rem" }}>
                    {section.section_instruction.split('\n').map((line, idx) => (
                      <li key={`section-inst-${idx}`}>{line.replace(/^•\s*/, '').trim()}</li>
                    ))}
                  </ul>
                </div>
              )}

              {section.questions
                .filter(({ data }) => data.is_subquestion !== "true")
                .map(({ data: question }, localIndex) => {
                  const currentIndex = ++localIndex;
                  const sub_ques = {};
                  const subIds = question.question_type === questionTypeConstants.SUBQUESTION.name && question.sub_questions
                    ? question.sub_questions.split(',').map(id => id.trim())
                    : [];

                  subIds.forEach(subId => {
                    if (subQuestions?.[subId]) {
                      sub_ques[subId] = subQuestions[subId];
                    }
                  });

                  return (
                    <div className="pb-5 question-container mx-0" key={localIndex}>
                      {question?.is_passage && question?.passage_id && question?.passage_content && (
                        <>
                          <div className="mb-5 mt-5">
                            <ViewEditor editorState={getQuestionPassageContent(question.passage_content)} />
                          </div>
                          <div className="col-md-12 separator separator-dashed separator-border-2 my-5"></div>
                        </>
                      )}

                      <div className="row">
                        <div className="col-md-12 d-flex justify-content-between">
                          <h6 className="d-flex align-items-center">Question {currentIndex}:</h6>
                          {sections.length > 0 && question.question_type !== questionTypeConstants.SUBQUESTION.name && (
                            <div>
                              {question?.is_optional && typeof candidateAnswerMap[question.question_id]?.mark_obtained === "number" &&
                                getSelectedQuestionIdsForMarking().has(question?.question_id) && (
                                  <span className="badge badge-success mr-2">
                                    Optional Question - Marks included in Total
                                  </span>
                                )}
                              {question?.is_optional && typeof candidateAnswerMap[question.question_id]?.mark_obtained === "number" &&
                                !getSelectedQuestionIdsForMarking().has(question?.question_id) && (
                                  <span className="badge badge-primary mr-2">
                                    Optional Question - Marks omitted in Total
                                  </span>
                                )}
                            </div>
                          )}
                          {question.question_type !== questionTypeConstants.SUBQUESTION.name && (
                            <Marks
                              viewAllocated={true}
                              label={question.mark_allocation}
                              value={state.questions?.[question.question_id]?.mark_obtained}
                              onChangeMarks={(e) => onChangeMarks(e, localIndex, question)}
                              mark_button_visible={true}
                              // readOnly={getEditability()}
                              readOnly={
                                getEditability() ||
                                isMCQReadOnly(question)
                              }
                              question={localIndex}
                            />
                          )}
                        </div>
                        <div className="col-md-12 separator separator-dashed separator-border-2 my-5"></div>
                      </div>

                      <p className="d-flex m-0">
                        <p className="col-md-2 d-flex align-items-start pt-3"><b>Question:</b></p>
                        <div className="col-md-10 px-0">{getContentInEditor(question, localIndex)}</div>
                      </p>

                      {question.question_type !== questionTypeConstants.SUBQUESTION.name && (
                        <>
                          <p className="d-flex m-0">
                            <p className="col-md-2 d-flex align-items-start pt-4"><b>Your Answer:</b></p>
                            <div className="col-md-10 px-0 answer-sheet-text">{getAnswerInEditor(question, localIndex)}</div>
                          </p>
                          <p className="d-flex m-0">
                            <p className="col-md-2 d-flex align-items-start pt-4"><b>Model Answer:</b></p>
                            <div className="col-md-10 px-0">{getAssessorAnswerInEditor(question, localIndex)}</div>
                          </p>
                          <p className="d-flex m-0 pb-1">
                            <p className="col-md-2 d-flex align-items-start"><b>Guidelines:</b></p>
                            <div className="col-md-10 px-0">{getAssessorAnswerGuidanceInEditor(question, localIndex)}</div>
                          </p>
                        </>
                      )}

                      {Object.keys(sub_ques).length > 0 && (
                        <div className="ml-5">
                          {Object.entries(sub_ques).map(([key, subQ], j) => (
                            <div key={key}>
                              <div className="col-md-12 separator separator-dashed separator-border-2 my-5"></div>
                              <div className="row">
                                <div className="col-md-12 d-flex justify-content-between">
                                  <h6 className="d-flex align-items-center">SubQuestion {j + 1}:</h6>
                                  {sections.length > 0 && (
                                    <div>
                                      {subQ?.is_optional && typeof candidateAnswerMap[subQ.question_id]?.mark_obtained === "number" &&
                                        getSelectedQuestionIdsForMarking().has(subQ?.question_id) && (
                                          <span className="badge badge-success mr-2">
                                            Optional Question - Marks included in Total
                                          </span>
                                        )}
                                      {subQ?.is_optional && typeof candidateAnswerMap[subQ.question_id]?.mark_obtained === "number" &&
                                        !getSelectedQuestionIdsForMarking().has(subQ?.question_id) && (
                                          <span className="badge badge-primary mr-2">
                                            Optional Question - Marks omitted in Total
                                          </span>
                                        )}
                                    </div>
                                  )}
                                  <Marks
                                    viewAllocated={true}
                                    label={subQ.mark_allocation}
                                    value={state.questions?.[subQ.question_id]?.mark_obtained}
                                    onChangeMarks={(e) => onChangeMarks(e, j, subQ)}
                                    mark_button_visible={true}
                                    // readOnly={getEditability()}
                                    readOnly={
                                      getEditability() ||
                                      isMCQReadOnly(subQ)
                                    }
                                    question={j}
                                  />
                                </div>
                                <div className="col-md-12 separator separator-dashed separator-border-2 my-5"></div>
                              </div>

                              <p className="d-flex m-0">
                                <p className="col-md-2 d-flex align-items-start pt-3"><b>Question:</b></p>
                                <div className="col-md-10 px-0">{getContentInEditor(subQ, j)}</div>
                              </p>
                              <p className="d-flex m-0">
                                <p className="col-md-2 d-flex align-items-start pt-4"><b>Your Answer:</b></p>
                                <div className="col-md-10 px-0 answer-sheet-text">{getAnswerInEditor(subQ, j)}</div>
                              </p>
                              <p className="d-flex m-0">
                                <p className="col-md-2 d-flex align-items-start pt-4"><b>Model Answer:</b></p>
                                <div className="col-md-10 px-0">{getAssessorAnswerInEditor(subQ, j)}</div>
                              </p>
                              <p className="d-flex m-0 pb-1">
                                <p className="col-md-2 d-flex align-items-start"><b>Guidelines:</b></p>
                                <div className="col-md-10 px-0">{getAssessorAnswerGuidanceInEditor(subQ, j)}</div>
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

export default SelfGradeGroupQuestionView;
